import { useState, useEffect } from "react";

interface InterestPopupProps {
  onClose: () => void;
}

export default function InterestPopup({ onClose }: InterestPopupProps) {
  const [visible, setVisible] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    investmentAmount: "",
    message: "",
  });

  useEffect(() => {
    // Slight delay for smooth entrance animation
    const t = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(t);
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 350);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formData.name || !formData.email) return;

    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));

      await fetch("https://readdy.ai/api/form/d7754a4ggtbbtt19rdcg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{
        backgroundColor: "rgba(7,26,46,0.65)",
        backdropFilter: "blur(4px)",
        opacity: visible ? 1 : 0,
        transition: "opacity 0.35s ease",
      }}
      onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
    >
      <div
        className="relative w-full max-w-lg rounded-3xl overflow-hidden"
        style={{
          background: "#fff",
          transform: visible ? "translateY(0) scale(1)" : "translateY(24px) scale(0.97)",
          transition: "transform 0.35s cubic-bezier(0.34,1.56,0.64,1)",
        }}
      >
        {/* Gold top bar */}
        <div className="h-[3px] w-full" style={{ background: "linear-gradient(90deg, transparent, #C9A84C 25%, #E2C27A 50%, #C9A84C 75%, transparent)" }} />

        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 hover:bg-slate-200 transition-colors cursor-pointer z-10"
        >
          <i className="ri-close-line text-slate-500 text-base leading-none" />
        </button>

        {submitted ? (
          /* ── Success state ── */
          <div className="px-10 py-14 text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#C9A84C]/15 mx-auto mb-5">
              <i className="ri-check-double-line text-[#C9A84C] text-3xl leading-none" />
            </div>
            <h3 className="text-2xl font-bold text-[#071A2E] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              Thank You
            </h3>
            <p className="text-slate-500 text-sm leading-relaxed mb-6">
              Your expression of interest has been received. A member of our investment team will be in touch within one business day.
            </p>
            <button
              onClick={handleClose}
              className="px-8 py-3 rounded-xl text-sm font-bold cursor-pointer whitespace-nowrap transition-all hover:brightness-110"
              style={{ background: "linear-gradient(135deg, #C9A84C 0%, #E2C27A 50%, #C9A84C 100%)", color: "#071A2E" }}
            >
              Close
            </button>
          </div>
        ) : (
          /* ── Form state ── */
          <div className="px-8 md:px-10 pt-8 pb-10">
            {/* Header */}
            <div className="flex items-start gap-4 mb-7">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#071A2E] flex-shrink-0">
                <i className="ri-home-4-fill text-[#C9A84C] text-xl leading-none" />
              </div>
              <div>
                <p className="text-[#C9A84C] text-[10px] font-bold uppercase tracking-[0.2em] mb-1">Thorpe Dellow</p>
                <h3 className="text-xl font-bold text-[#071A2E] leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Express Your Interest
                </h3>
                <p className="text-slate-500 text-xs mt-1 leading-relaxed">
                  Speak with our investment team about the 10% p.a. Social Housing Structured Product.
                </p>
              </div>
            </div>

            {/* Key stat strip */}
            <div className="flex items-center gap-4 bg-[#EEF2F9] rounded-xl px-5 py-3 mb-7">
              {[
                { value: "10.00% p.a.", label: "Target Return" },
                { value: "5 Years", label: "Fixed Term" },
                { value: "1st Charge", label: "Security" },
              ].map((s) => (
                <div key={s.label} className="flex-1 text-center">
                  <p className="text-[#071A2E] font-bold text-sm leading-none" style={{ fontFamily: "'Playfair Display', serif" }}>{s.value}</p>
                  <p className="text-slate-400 text-[10px] mt-0.5 uppercase tracking-wide">{s.label}</p>
                </div>
              ))}
            </div>

            <form
              data-readdy-form
              id="properties-interest-popup"
              onSubmit={handleSubmit}
              className="flex flex-col gap-4"
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-slate-600">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="John Smith"
                    className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-slate-700 focus:outline-none focus:border-[#C9A84C] transition-colors placeholder-slate-300"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-slate-600">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="john@example.com"
                    className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-slate-700 focus:outline-none focus:border-[#C9A84C] transition-colors placeholder-slate-300"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-slate-600">Phone Number</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+44 7700 000000"
                    className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-slate-700 focus:outline-none focus:border-[#C9A84C] transition-colors placeholder-slate-300"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-slate-600">Investment Amount</label>
                  <select
                    name="investmentAmount"
                    value={formData.investmentAmount}
                    onChange={handleChange}
                    className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-slate-600 focus:outline-none focus:border-[#C9A84C] transition-colors cursor-pointer"
                  >
                    <option value="">Select range</option>
                    <option value="£25,000 - £50,000">£25,000 – £50,000</option>
                    <option value="£50,000 - £100,000">£50,000 – £100,000</option>
                    <option value="£100,000 - £250,000">£100,000 – £250,000</option>
                    <option value="£250,000+">£250,000+</option>
                  </select>
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-xs font-semibold text-slate-600">Message (optional)</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Any questions or specific requirements..."
                  rows={3}
                  maxLength={500}
                  className="bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-slate-700 focus:outline-none focus:border-[#C9A84C] transition-colors placeholder-slate-300 resize-none"
                />
                <p className="text-[10px] text-slate-300 text-right">{formData.message.length}/500</p>
              </div>

              <button
                type="submit"
                disabled={submitting || !formData.name || !formData.email}
                className="w-full py-3.5 rounded-xl font-bold text-sm uppercase tracking-wide cursor-pointer whitespace-nowrap transition-all hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed mt-1"
                style={{ background: "linear-gradient(135deg, #C9A84C 0%, #E2C27A 50%, #C9A84C 100%)", color: "#071A2E" }}
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin leading-none" />
                    Sending...
                  </span>
                ) : (
                  "Request a Callback"
                )}
              </button>

              <p className="text-[10px] text-slate-400 text-center leading-relaxed">
                Capital is at risk. Directed at Certified HNW &amp; Sophisticated Investors only.
              </p>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
